if (!customElements.get('shipping-cutoff-timer')) {
  // The warehouse ships on Danish time, wherever the visitor is; the time zone
  // also takes care of summer and winter time.
  const SHIPPING_TIME = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Copenhagen',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hourCycle: 'h23',
  });

  const SHIPPING_WEEKDAYS = new Set(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);

  // Danish public holidays that fall on weekdays, as MM-DD: the fixed ones, and
  // the ones that move with Easter (Maundy Thursday, Good Friday, Easter Monday,
  // Ascension Day, Whit Monday).
  const FIXED_HOLIDAYS = ['01-01', '12-25', '12-26'];
  const EASTER_OFFSETS = [-3, -2, 1, 39, 50];
  const holidaysByYear = new Map();

  // Easter Sunday in the Gregorian calendar (the anonymous Gregorian algorithm).
  function getEasterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const h = (19 * a + b - Math.floor(b / 4) - Math.floor((b - Math.floor((b + 8) / 25) + 1) / 3) + 15) % 30;
    const l = (32 + 2 * (b % 4) + 2 * Math.floor(c / 4) - h - (c % 4)) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;

    return Date.UTC(year, month - 1, day);
  }

  function getPublicHolidays(year) {
    if (!holidaysByYear.has(year)) {
      const easter = getEasterSunday(year);
      const moving = EASTER_OFFSETS.map((offset) => new Date(easter + offset * 86_400_000).toISOString().slice(5, 10));
      holidaysByYear.set(year, new Set([...FIXED_HOLIDAYS, ...moving]));
    }

    return holidaysByYear.get(year);
  }

  // The theme setting's extra days, one per line: 24-12 is every 24 December,
  // 2026-11-27 is that date only. Kept as MM-DD and YYYY-MM-DD.
  function parseClosedDays(value = '') {
    const days = new Set();

    for (const entry of value.split(/[\s,;]+/)) {
      const yearly = /^(\d{1,2})[-./](\d{1,2})$/.exec(entry);
      const once = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(entry);

      if (yearly) days.add(`${yearly[2].padStart(2, '0')}-${yearly[1].padStart(2, '0')}`);
      if (once) days.add(`${once[1]}-${once[2].padStart(2, '0')}-${once[3].padStart(2, '0')}`);
    }

    return days;
  }

  function getShippingTimeParts(date) {
    return Object.fromEntries(
      SHIPPING_TIME.formatToParts(date)
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, part.value])
    );
  }

  function isShippingDay(parts, closedDays) {
    if (!SHIPPING_WEEKDAYS.has(parts.weekday)) return false;

    const monthDay = `${parts.month}-${parts.day}`;

    if (getPublicHolidays(Number(parts.year)).has(monthDay)) return false;

    return !closedDays.has(monthDay) && !closedDays.has(`${parts.year}-${monthDay}`);
  }

  // Seconds left until today's cutoff, 0 after it or on a day without shipping.
  function getRemainingSecondsUntilCutoff(cutoffHour, closedDays, date = new Date()) {
    const parts = getShippingTimeParts(date);

    if (!isShippingDay(parts, closedDays)) return 0;

    const elapsedSeconds =
      Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second);
    const remainingSeconds = cutoffHour * 3600 - elapsedSeconds;

    return Math.max(0, remainingSeconds);
  }

  function formatCountdown(totalSeconds) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
  }

  // The t filter HTML-escapes the copy (we'll -> we&#39;ll) and it is set as plain text.
  function decodeEntities(text) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  class ShippingCutoffTimer extends HTMLElement {
    connectedCallback() {
      this.cutoffHour = Number(this.dataset.cutoffHour) || 15;
      this.closedDays = parseClosedDays(this.dataset.closedDays);

      try {
        const copy = JSON.parse(this.querySelector('[data-shipping-cutoff-i18n]')?.textContent || '{}');
        this.i18n = Object.fromEntries(Object.entries(copy).map(([key, value]) => [key, decodeEntities(value)]));
      } catch {
        this.i18n = {};
      }

      this.linePrimary = this.querySelector('[data-shipping-cutoff-line-primary]');
      this.lineSecondary = this.querySelector('[data-shipping-cutoff-line-secondary]');
      this.timeEl = this.querySelector('[data-shipping-cutoff-time]');

      this.update();
      this.timer = window.setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    setWeekdayView(remainingSeconds) {
      const time = formatCountdown(remainingSeconds);
      const prefix = this.i18n.weekday_line_1 || '';
      const suffix = this.i18n.weekday_line_2 || '';

      if (this.timeEl) {
        this.timeEl.textContent = time;
        this.timeEl.hidden = false;
      }

      if (this.linePrimary) {
        this.linePrimary.textContent = prefix ? `${prefix} ` : '';
        this.linePrimary.hidden = false;
      }

      if (this.lineSecondary) {
        this.lineSecondary.textContent = suffix;
        this.lineSecondary.hidden = suffix === '';
      }

      this.hidden = false;
    }

    setWeekendView() {
      if (this.linePrimary) {
        this.linePrimary.textContent = this.i18n.weekend_line_1 || '';
        this.linePrimary.hidden = false;
      }

      if (this.timeEl) {
        this.timeEl.hidden = true;
      }

      if (this.lineSecondary) {
        this.lineSecondary.textContent = this.i18n.weekend_line_2 || '';
        this.lineSecondary.hidden = false;
      }

      this.hidden = false;
    }

    update() {
      if (this.dataset.eligible !== 'true') {
        this.hidden = true;
        return;
      }

      // After the cutoff, on weekends and on holidays alike: ships next business day.
      const remainingSeconds = getRemainingSecondsUntilCutoff(this.cutoffHour, this.closedDays);

      if (remainingSeconds <= 0) {
        this.setWeekendView();
        return;
      }

      this.setWeekdayView(remainingSeconds);
    }
  }

  customElements.define('shipping-cutoff-timer', ShippingCutoffTimer);
}
